import { z } from "zod";
import {
  CautionaryProfileSchema,
  LevelBandSchema,
  ModelLevelsSchema,
  ReferenceProfileSchema,
} from "./level-assessment-model";

/**
 * @public — the file format for importing a competency matrix (JSON or YAML)
 * into the authoring wizard.
 *
 * This is the **contract** an external "matrix-authoring" skill targets: it
 * produces a file in this shape, the operator uploads it, and the importer
 * populates the wizard draft (which the operator then reviews + submits through
 * the normal create/update flow). The shape deliberately MIRRORS the data model
 * + the four pillars (see `docs/labs/level-assessment/competency-matrix-fields.md`)
 * so the mapping to the editor draft is mechanical.
 *
 * Two top-level shapes:
 * - `ModelMatrixImportSchema` — a whole model's **cross** competencies (cross-only;
 *   disciplines are imported separately).
 * - `DisciplineMatrixImportSchema` — one discipline's **vertical** competencies.
 *
 * Level keys (in `cells` / `boundaries` / `levels.names`) are strings. YAML
 * integer keys deserialize to JS string object keys, so both `"4":` and `4:`
 * work in source files.
 */

/** One axis (competency) in an import file — identity + the four pillars. Only
 *  `label` is required; the importer fills what's present and leaves the rest
 *  blank for the operator to complete (the wizard's "Recomendações" advisory
 *  surfaces the MVP gaps). */
export const ImportAxisSchema = z.object({
  /** Human label (e.g. "Qualidade de código"). */
  label: z.string().min(1),
  /** Pillar 1 · what it measures (one sentence). */
  description: z.string().optional(),
  /** Aggregation weight (> 0). Defaults to 1 downstream when omitted. */
  weight: z.number().positive().finite().optional(),
  /** Pillar 1 · what it is NOT. */
  notAbout: z.string().optional(),
  /** Pillar 1 · baseline level (a level number in the range). */
  baseline: z.number().int().nonnegative().optional(),
  /** Pillar 3 · what doesn't count (prestige / title / brand traps). */
  falsePositives: z.string().optional(),
  /** Pillar 3 · hard cap (ceiling rule). */
  hardCap: z.string().optional(),
  /** Pillar 4 · cautionary profiles (commonly mis-judged). */
  cautionaryProfiles: z.array(CautionaryProfileSchema).optional(),
  /** Per-level content, keyed by level number as a string:
   *  Pillar 2 `def` + Pillar 3 `whatCounts`/`easyToMiss` + Pillar 4
   *  `referenceProfiles`. */
  cells: z
    .record(
      z.string(),
      z.object({
        def: z.string().optional(),
        whatCounts: z.string().optional(),
        easyToMiss: z.string().optional(),
        referenceProfiles: z.array(ReferenceProfileSchema).optional(),
      }),
    )
    .optional(),
  /** Pillar 2 · "crosses-when" boundaries, keyed by the LOWER level of each
   *  adjacent pair (`"3"` = the L3→L4 crossing). */
  boundaries: z.record(z.string(), z.string()).optional(),
});
export type ImportAxis = z.infer<typeof ImportAxisSchema>;

/** A whole model's cross competencies. `name`/`description` are optional —
 *  the operator can also type them in the wizard. Disciplines are NOT part of a
 *  model import (imported separately as discipline files). */
export const ModelMatrixImportSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  levels: ModelLevelsSchema,
  crossAxes: z.array(ImportAxisSchema).min(1).max(20),
});
export type ModelMatrixImport = z.infer<typeof ModelMatrixImportSchema>;

/** One discipline's vertical competencies. `levels` is the discipline's optional
 *  continuous sub-band of the model range (absent = the full model range); the
 *  actual model range comes from the model the discipline is added to, not the
 *  file. */
export const DisciplineMatrixImportSchema = z.object({
  label: z.string().min(1),
  description: z.string().optional(),
  levels: LevelBandSchema.optional(),
  axes: z.array(ImportAxisSchema).min(1).max(20),
});
export type DisciplineMatrixImport = z.infer<typeof DisciplineMatrixImportSchema>;

// ── Export (download) ──────────────────────────────────────────────────────
//
// The download counterpart of the import contract above: one JSON file with
// the WHOLE matrix — the cross block in the model-import shape plus every
// discipline in the discipline-import shape under `disciplines`. Designed to
// round-trip: `ModelMatrixImportSchema` accepts an export file as-is (Zod
// strips the extra `id`/`disciplines` keys), and each `disciplines[i]` entry
// parses with `DisciplineMatrixImportSchema`. The stable axis/discipline ids
// are included as informative metadata (they key signal routing and per-axis
// results); the importer ignores them and re-derives ids from labels.

/** An exported axis — the import shape + the persisted axis id. */
export const ExportAxisSchema = ImportAxisSchema.extend({
  id: z.string(),
});
export type ExportAxis = z.infer<typeof ExportAxisSchema>;

export const DisciplineMatrixExportSchema = DisciplineMatrixImportSchema.extend({
  id: z.string(),
  axes: z.array(ExportAxisSchema).min(1).max(20),
});
export type DisciplineMatrixExport = z.infer<typeof DisciplineMatrixExportSchema>;

/** The full downloaded competency matrix: cross + all disciplines. */
export const ModelMatrixExportSchema = ModelMatrixImportSchema.extend({
  id: z.string(),
  crossAxes: z.array(ExportAxisSchema).min(1).max(20),
  disciplines: z.array(DisciplineMatrixExportSchema),
});
export type ModelMatrixExport = z.infer<typeof ModelMatrixExportSchema>;
